import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
  type TablePaginationConfig,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  serviceBonusService,
  type CreateServiceBonusRequest,
  type GetServiceBonusParams,
  type ServiceBonus,
  type UpdateServiceBonusStatusRequest,
} from "../services/serviceBonusService";

const { Title } = Typography;
const { Option } = Select;

const ServiceBonusPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  // States
  const [searchParams, setSearchParams] = useState<GetServiceBonusParams>({
    page: 1,
    limit: 10,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingServiceBonus, setEditingServiceBonus] =
    useState<ServiceBonus | null>(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  // ✅ Query - เพิ่ม refetch
  const {
    data: serviceBonusesResponse,
    isLoading,
    refetch, // ✅ เพิ่มตรงนี้
  } = useQuery({
    queryKey: ["serviceBonus", searchParams],
    queryFn: () => serviceBonusService.getServiceBonuses(searchParams),
    keepPreviousData: true, // ✅ ใช้ keepPreviousData แทน placeholderData
    onError: (error: Error) => {
      message.error("ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້");
      console.error("Error fetching serviceBonus:", error);
    },
  });

  // ✅ Create Mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateServiceBonusRequest) =>
      serviceBonusService.createServiceBonus(data),
    onSuccess: () => {
      message.success("ເພີ່ມລາຍການສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: ["serviceBonus"] });
      handleModalClose();
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ✅ Update Mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: CreateServiceBonusRequest;
    }) => serviceBonusService.updateServiceBonus(id, data),
    onSuccess: () => {
      message.success("ແກ້ໄຂຂໍ້ມູນສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: ["serviceBonus"] });
      handleModalClose();
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateServiceBonusStatusRequest;
    }) => serviceBonusService.updateServiceBonusStatus(id, data),
    onSuccess: () => {
      message.success("ປ່ຽນສະຖານະສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: ["serviceBonus"] });
      handleModalClose();
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "ເກີດຂໍ້ຜິດພາດ");
    },
  });

  // ✅ Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => serviceBonusService.deleteServiceBonus(id),
    onSuccess: () => {
      message.success("ລຶບຂໍ້ມູນສຳເລັດ");
      queryClient.invalidateQueries({ queryKey: ["serviceBonus"] });
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "ເກີດຂໍ້ຜິດພາດໃນການລຶບ");
    },
  });

  // ✅ Handlers
  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingServiceBonus(null);
    form.resetFields();
  };

  const handleSubmit = (values: any) => {
    const formData: CreateServiceBonusRequest = {
      name: values.name,
      price: Number(values.price),
      note: values.note,
    };

    if (editingServiceBonus) {
      updateMutation.mutate({ id: editingServiceBonus._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (record: ServiceBonus) => {
    setEditingServiceBonus(record);
    form.setFieldsValue({
      name: record.name,
      price: record.price,
      note: record.note,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (record: ServiceBonus) => {
    Modal.confirm({
      title: "ຢືນຢັນການລຶບ",
      content: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບ "${record.name}"?`,
      okText: "ລຶບ",
      okType: "danger",
      cancelText: "ຍົກເລິກ",
      onOk: () => {
        deleteMutation.mutate(record._id);
      },
    });
  };

  const handleChangeStatus = (record: ServiceBonus) => {
    const data:UpdateServiceBonusStatusRequest = {
        status: record?.status === 'active' ? 'inactive' : 'active'
    }
    updateStatusMutation.mutate({id:record?._id, data:data});
  };

  const handleViewDetails = (record: ServiceBonus) => {
    Modal.info({
      title: "ລາຍລະອຽດ",
      width: 600,
      okText: "ປິດອອກ",
      content: (
        <div style={{ marginTop: 16 }}>
          <p>
            <strong>ຊື່:</strong> {record.name}
          </p>
          <p>
            <strong>ລາຄາ:</strong>{" "}
            {record.price?.toLocaleString("lo-LA") || "N/A"} LAK
          </p>
          <p>
            <strong>ສະຖານະ:</strong> {record.status || "N/A"}
          </p>
          <p>
            <strong>ໝາຍເຫດ:</strong> {record.note || "ບໍ່ມີ"}
          </p>
        </div>
      ),
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setSearchParams((prev) => ({
      ...prev,
      page: 1,
      name: value || undefined,
    }));
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value);
    setSearchParams((prev) => ({
      ...prev,
      page: 1,
      status: value || undefined,
    }));
  };

  // ✅ แก้ไข Table onChange
  const handleTableChange = (pagination: TablePaginationConfig) => {
    setSearchParams((prev) => ({
      ...prev,
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
    }));
  };

  // ✅ Table Columns
  const columns = [
    {
      title: "ຊື່ລາຍການ",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <Space>{name}</Space>,
    },
    {
      title: "ລາຄາ (LAK)",
      dataIndex: "price",
      key: "price",
      render: (price: number) => (
        <span style={{ fontWeight: 500 }}>
          {price?.toLocaleString("lo-LA") || "0"} ₭
        </span>
      ),
    },
    {
      title: "ສະຖານະ",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: ServiceBonus) => (
        <>
          <Tooltip
            title={
              status === "active" ? "ກົດເພື່ອປິດໃຊ້ງານ" : "ກົດເພື່ອເປິດໃຊ້ງານ"
            }
          >
            <span
              style={{
                padding: "4px 12px",
                borderRadius: "4px",
                cursor: "pointer",
                background: status === "active" ? "#f6ffed" : "#fff1f0",
                color: status === "active" ? "#52c41a" : "#ff4d4f",
                border: `1px solid ${
                  status === "active" ? "#b7eb8f" : "#ffa39e"
                }`,
              }}
              onClick={() => handleChangeStatus(record)}
            >
              {status === "active" ? "ພ້ອມໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
            </span>
          </Tooltip>
        </>
      ),
    },
    {
      title: "ໝາຍເຫດ",
      dataIndex: "note",
      key: "note",
      render: (note: string) => note || "-",
    },
    {
      title: "ຈັດການ",
      key: "actions",
      width: 250,
      fixed: "right" as const,
      render: (_: any, record: ServiceBonus) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            ເບິ່ງ
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            loading={
              updateMutation.isLoading &&
              editingServiceBonus?._id === record._id
            }
          >
            ແກ້ໄຂ
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            loading={deleteMutation.isLoading}
          >
            ລຶບ
          </Button>
        </Space>
      ),
    },
  ];

  // ✅ Data
  const serviceBonuses = serviceBonusesResponse?.data?.serviceBonuses || [];
  const pagination = serviceBonusesResponse?.data?.pagination;

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          ຈັດການເງິນພິເສດໃນການບໍລິການ
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetch()}
          loading={isLoading}
        >
          ຮີເຟສ
        </Button>
      </div>

      {/* Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Input.Search
            placeholder="ຄົ້ນຫາຊື່ລາຍການ..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
            enterButton
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder="ເບິ່ງຕາມສະຖານະ"
            allowClear
            value={statusFilter}
            onChange={handleStatusFilter}
            style={{ width: "100%" }}
          >
            <Option value="active">ພ້ອມໃຊ້ງານ</Option>
            <Option value="inactive">ປິດໃຊ້ງານ</Option>
          </Select>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
            style={{ width: "100%" }}
            loading={createMutation.isLoading}
          >
            ເພີ່ມລາຍການໃໝ່
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={serviceBonuses}
          rowKey="_id"
          loading={isLoading || updateStatusMutation.isLoading}
          onChange={handleTableChange}
          pagination={{
            current: pagination?.current || 1,
            pageSize: searchParams.limit || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={
          editingServiceBonus ? (
            <Space>
              <EditOutlined />
              ແກ້ໄຂຂໍ້ມູນ
            </Space>
          ) : (
            <Space>
              <PlusOutlined />
              ເພີ່ມລາຍການໃໝ່
            </Space>
          )
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Spin spinning={createMutation.isLoading || updateMutation.isLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ marginTop: 24 }}
          >
            <Form.Item
              name="name"
              label="ຊື່ລາຍການ"
              rules={[
                { required: true, message: "ກະລຸນາປ້ອນຊື່ລາຍການ" },
                { min: 3, message: "ຊື່ລາຍການຕ້ອງມີຢ່າງໜ້ອຍ 3 ຕົວອັກສອນ" },
              ]}
            >
              <Input placeholder="ຊື່ລາຍການ" size="large" />
            </Form.Item>

            <Form.Item
              name="price"
              label="ຈຳນວນເງິນ (LAK)"
              rules={[
                { required: true, message: "ກະລຸນາປ້ອນຈຳນວນເງິນ" },
                {
                  validator: (_, value) => {
                    if (value && value < 0) {
                      return Promise.reject("ຈຳນວນເງິນຕ້ອງຫຼາຍກວ່າ 0");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                type="number"
                min={0}
                placeholder="0"
                size="large"
                suffix="₭"
              />
            </Form.Item>

            <Form.Item name="note" label="ໝາຍເຫດ">
              <TextArea
                rows={4}
                placeholder="ໝາຍເຫດເພີ່ມເຕີມ (ບໍ່ບັງຄັບ)"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button onClick={handleModalClose}>ຍົກເລິກ</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={createMutation.isLoading || updateMutation.isLoading}
                  icon={
                    editingServiceBonus ? <EditOutlined /> : <PlusOutlined />
                  }
                >
                  {editingServiceBonus ? "ອັບເດດ" : "ເພີ່ມ"}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default ServiceBonusPage;
